import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { FormLabelItem, TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { PrivateDocumentCategory } from '@/shared/lib/sj-orm/models/document/private.document.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import { AssetCategory, AssetDto } from '../../sj-orm/models/asset.dto';
import {
  PreciousGemstonesDetails,
  PreciousGemstonesGeneral,
} from '../../sj-orm/models/assets/categories/preciousGemstones';
import {
  AppraisalLaboratoryType,
  ClarityType,
  ColorType,
  CutAndPolishType,
  FluorescenceType,
  PreciousGemstonesType,
  ShapeType,
  TypeOfHistoryOfOriginType,
  TypeOfOrigin,
} from '../../sj-orm/models/assets/enums';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type PreciousGemstones = PreciousGemstonesFormGeneralType &
  PreciousGemstonesFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

export const PreciousGemstonesList = [
  {
    label: 'forms_assets:gemstones.enums.type.diamond',
    key: PreciousGemstonesType.diamond,
  },
  {
    label: 'forms_assets:gemstones.enums.type.ruby',
    key: PreciousGemstonesType.ruby,
  },
  {
    label: 'forms_assets:gemstones.enums.type.emerald',
    key: PreciousGemstonesType.emerald,
  },
  {
    label: 'forms_assets:gemstones.enums.type.sapphire',
    key: PreciousGemstonesType.sapphire,
  },
  {
    label: 'forms_assets:gemstones.enums.type.other',
    key: PreciousGemstonesType.other,
  },
];

export const TypeOfOriginList = [
  {
    label: 'forms_assets:gemstones.enums.origin.natural',
    key: TypeOfOrigin.natural,
  },
  {
    label: 'forms_assets:gemstones.enums.origin.synthetic',
    key: TypeOfOrigin.synthetic,
  },
];

const AppraisalLaboratoryList = [
  {
    label: 'forms_assets:gemstones.enums.appraisalLaboratory.GIA',
    key: AppraisalLaboratoryType.GIA,
  },
  {
    label: 'forms_assets:gemstones.enums.appraisalLaboratory.HRD',
    key: AppraisalLaboratoryType.HRD,
  },
  {
    label: 'forms_assets:gemstones.enums.appraisalLaboratory.IGI',
    key: AppraisalLaboratoryType.IGI,
  },
  {
    label: 'forms_assets:gemstones.enums.appraisalLaboratory.EGL',
    key: AppraisalLaboratoryType.EGL,
  },
  {
    label: 'forms_assets:gemstones.enums.appraisalLaboratory.GAGLT',
    key: AppraisalLaboratoryType.GAGLT,
  },
  {
    label: 'forms_assets:gemstones.enums.appraisalLaboratory.ISDI',
    key: AppraisalLaboratoryType.ISDI,
  },
  {
    label: 'forms_assets:gemstones.enums.appraisalLaboratory.INDI',
    key: AppraisalLaboratoryType.INDI,
  },
  {
    label: 'forms_assets:gemstones.enums.appraisalLaboratory.other',
    key: AppraisalLaboratoryType.OTHER,
  },
];

const ColorList = [
  {
    label: 'forms_assets:gemstones.enums.color.D',
    key: ColorType.D,
  },
  {
    label: 'forms_assets:gemstones.enums.color.E',
    key: ColorType.E,
  },
  {
    label: 'forms_assets:gemstones.enums.color.F',
    key: ColorType.F,
  },
  {
    label: 'forms_assets:gemstones.enums.color.G',
    key: ColorType.G,
  },
  {
    label: 'forms_assets:gemstones.enums.color.H',
    key: ColorType.H,
  },
  {
    label: 'forms_assets:gemstones.enums.color.I',
    key: ColorType.I,
  },
  {
    label: 'forms_assets:gemstones.enums.color.J',
    key: ColorType.J,
  },
  {
    label: 'forms_assets:gemstones.enums.color.K',
    key: ColorType.K,
  },
  {
    label: 'forms_assets:gemstones.enums.color.L',
    key: ColorType.L,
  },
  {
    label: 'forms_assets:gemstones.enums.color.M',
    key: ColorType.M,
  },
  {
    label: 'forms_assets:gemstones.enums.color.N',
    key: ColorType.N,
  },
  {
    label: 'forms_assets:gemstones.enums.color.O',
    key: ColorType.O,
  },
  {
    label: 'forms_assets:gemstones.enums.color.P',
    key: ColorType.P,
  },
  {
    label: 'forms_assets:gemstones.enums.color.Q',
    key: ColorType.Q,
  },
  {
    label: 'forms_assets:gemstones.enums.color.R',
    key: ColorType.R,
  },
  {
    label: 'forms_assets:gemstones.enums.color.S',
    key: ColorType.S,
  },
  {
    label: 'forms_assets:gemstones.enums.color.T',
    key: ColorType.T,
  },
  {
    label: 'forms_assets:gemstones.enums.color.U',
    key: ColorType.U,
  },
  {
    label: 'forms_assets:gemstones.enums.color.V',
    key: ColorType.V,
  },
  {
    label: 'forms_assets:gemstones.enums.color.W',
    key: ColorType.W,
  },
  {
    label: 'forms_assets:gemstones.enums.color.X',
    key: ColorType.X,
  },
  {
    label: 'forms_assets:gemstones.enums.color.Y',
    key: ColorType.Y,
  },
  {
    label: 'forms_assets:gemstones.enums.color.Z',
    key: ColorType.Z,
  },
  {
    label: 'forms_assets:gemstones.enums.color.other',
    key: ColorType.other,
  },
];

const ClarityList = [
  {
    label: 'forms_assets:gemstones.enums.clarityType.flawless',
    key: ClarityType.flawless,
  },
  {
    label: 'forms_assets:gemstones.enums.clarityType.internallyFlawless',
    key: ClarityType.internallyFlawless,
  },
  {
    label: 'forms_assets:gemstones.enums.clarityType.VVS1',
    key: ClarityType.VVS1,
  },
  {
    label: 'forms_assets:gemstones.enums.clarityType.VVS2',
    key: ClarityType.VVS2,
  },
  {
    label: 'forms_assets:gemstones.enums.clarityType.VS1',
    key: ClarityType.VS1,
  },
  {
    label: 'forms_assets:gemstones.enums.clarityType.VS2',
    key: ClarityType.VS2,
  },
  {
    label: 'forms_assets:gemstones.enums.clarityType.S1',
    key: ClarityType.S1,
  },
  {
    label: 'forms_assets:gemstones.enums.clarityType.S2',
    key: ClarityType.S2,
  },
  {
    label: 'forms_assets:gemstones.enums.clarityType.I1',
    key: ClarityType.I1,
  },
  {
    label: 'forms_assets:gemstones.enums.clarityType.I2',
    key: ClarityType.I2,
  },
  {
    label: 'forms_assets:gemstones.enums.clarityType.I3',
    key: ClarityType.I3,
  },
  {
    label: 'forms_assets:gemstones.enums.clarityType.other',
    key: ClarityType.other,
  },
];

const CutAndPolishList = [
  {
    label: 'forms_assets:gemstones.enums.cutAndPolish.excellent',
    key: CutAndPolishType.excellent,
  },
  {
    label: 'forms_assets:gemstones.enums.cutAndPolish.veryGood',
    key: CutAndPolishType.veryGood,
  },
  {
    label: 'forms_assets:gemstones.enums.cutAndPolish.good',
    key: CutAndPolishType.good,
  },
  {
    label: 'forms_assets:gemstones.enums.cutAndPolish.fair',
    key: CutAndPolishType.fair,
  },
  {
    label: 'forms_assets:gemstones.enums.cutAndPolish.poor',
    key: CutAndPolishType.poor,
  },
  {
    label: 'forms_assets:gemstones.enums.cutAndPolish.other',
    key: CutAndPolishType.other,
  },
];

const ShapeList = [
  {
    label: 'forms_assets:gemstones.enums.shape.cushion',
    key: ShapeType.cushion,
  },
  {
    label: 'forms_assets:gemstones.enums.shape.marquise',
    key: ShapeType.marquise,
  },
  {
    label: 'forms_assets:gemstones.enums.shape.radiant',
    key: ShapeType.radiant,
  },
  {
    label: 'forms_assets:gemstones.enums.shape.pear',
    key: ShapeType.pear,
  },
  {
    label: 'forms_assets:gemstones.enums.shape.round',
    key: ShapeType.round,
  },
  {
    label: 'forms_assets:gemstones.enums.shape.oval',
    key: ShapeType.oval,
  },
  {
    label: 'forms_assets:gemstones.enums.shape.emerald',
    key: ShapeType.emerald,
  },
  {
    label: 'forms_assets:gemstones.enums.shape.princess',
    key: ShapeType.princess,
  },
  {
    label: 'forms_assets:gemstones.enums.shape.heart',
    key: ShapeType.heart,
  },
  {
    label: 'forms_assets:gemstones.enums.shape.other',
    key: ShapeType.other,
  },
];

const FluorescenceList = [
  {
    label: 'forms_assets:gemstones.enums.fluorescence.none',
    key: FluorescenceType.none,
  },
  {
    label: 'forms_assets:gemstones.enums.fluorescence.slight',
    key: FluorescenceType.slight,
  },
  {
    label: 'forms_assets:gemstones.enums.fluorescence.medium',
    key: FluorescenceType.medium,
  },
  {
    label: 'forms_assets:gemstones.enums.fluorescence.strong',
    key: FluorescenceType.strong,
  },
  {
    label: 'forms_assets:gemstones.enums.fluorescence.veryStrong',
    key: FluorescenceType.veryStrong,
  },
  {
    label: 'forms_assets:gemstones.enums.fluorescence.other',
    key: ShapeType.other,
  },
];
const TypeOfHistoryOfOriginList = [
  {
    label: 'forms_assets:gemstones.enums.typeOfHistoryOfOrigin.gift',
    key: TypeOfHistoryOfOriginType.gift,
  },
  {
    label: 'forms_assets:gemstones.enums.typeOfHistoryOfOrigin.purchase',
    key: TypeOfHistoryOfOriginType.purchase,
  },
  {
    label: 'forms_assets:gemstones.enums.typeOfHistoryOfOrigin.inheritance',
    key: TypeOfHistoryOfOriginType.inheritance,
  },
  {
    label: 'forms_assets:gemstones.enums.typeOfHistoryOfOrigin.other',
    key: TypeOfHistoryOfOriginType.other,
  },
];

type PreciousGemstonesHelpGeneral = {
  nickName: QuestionType.TEXT;
  type: QuestionType.SELECT_FROM_LIST;
  otherType?: QuestionType.TEXT;
  typeOfOrigin: QuestionType.SELECT_FROM_LIST;
  purchaseValue: QuestionType.CURRENCY_CALCULATOR;
  weight: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  storageLocation: QuestionType.TEXT;
};

type PreciousGemstonesHelpDetails = {
  independentAppraisalValue?: QuestionType.CURRENCY_CALCULATOR;
  purchaseValueEquivalent?: QuestionType.NUMBER;
  independentAppraisalValueEquivalent?: QuestionType.NUMBER;
  appraisalLaboratory?: QuestionType.SELECT_FROM_LIST;
  otherAppraisalLaboratory?: QuestionType.TEXT;
  hasJewelry?: QuestionType.CHECKBOX;
  jewelry?: QuestionType.PICK_ARRAY_OF_DTO;
  color?: QuestionType.SELECT_FROM_LIST;
  otherColor?: QuestionType.TEXT;
  clarity?: QuestionType.SELECT_FROM_LIST;
  otherClarity?: QuestionType.TEXT;
  enhancements?: QuestionType.CHECKBOX;
  defects?: QuestionType.TEXT;
  cut?: QuestionType.SELECT_FROM_LIST;
  otherCut?: QuestionType.TEXT;
  sizes?: QuestionType.TEXT;
  countryOfOrigin?: QuestionType.TEXT;
  serialNumber?: QuestionType.TEXT;
  polish?: QuestionType.SELECT_FROM_LIST;
  otherPolish?: QuestionType.TEXT;
  shape?: QuestionType.SELECT_FROM_LIST;
  otherShape?: QuestionType.TEXT;
  fluorescence?: QuestionType.SELECT_FROM_LIST;
  otherFluorescence?: QuestionType.TEXT;
  typeOfHistoryOfOrigin?: QuestionType.SELECT_FROM_LIST;
  otherTypeOfHistoryOfOrigin?: QuestionType.TEXT;
  occasion?: QuestionType.TEXT;
  source?: QuestionType.TEXT;
  acquisitionDate?: QuestionType.DATE_TIME;
  purchaseReceipt?: QuestionType.PICK_ARRAY_OF_DTO;
  writeOffAccount?: QuestionType.TEXT;
  internationalCertificate?: QuestionType.PICK_ARRAY_OF_DTO;
  additionalAppraisal?: QuestionType.PICK_ARRAY_OF_DTO;
  attachments?: QuestionType.FILE_UPLOAD;
};

type PreciousGemstonesFormGeneralType = TDtoWrapper<
  PreciousGemstonesGeneral,
  PreciousGemstonesHelpGeneral
>;
type PreciousGemstonesFormDetailsType = TDtoWrapper<
  PreciousGemstonesDetails,
  PreciousGemstonesHelpDetails
>;

const PreciousGemstonesFormGeneral: PreciousGemstonesFormGeneralType = {
  nickName: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:gemstones.inputs.country.label',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:gemstones.inputs.type.label',
    value: PreciousGemstonesType.diamond,
    items: PreciousGemstonesList,
    required: true,
  },
  otherType: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.otherType.label',
    value: '',
    showIf: { type: { value: PreciousGemstonesType.other } },
  },
  typeOfOrigin: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:gemstones.inputs.typeOfOrigin.label',
    value: TypeOfOrigin.natural,
    required: true,
    items: TypeOfOriginList,
  },
  purchaseValue: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:gemstones.inputs.personalAppraisal.label',
    tooltip: 'forms_assets:gemstones.inputs.personalAppraisal.tooltip',
    equivalentValueTooltip:
      // eslint-disable-next-line sonarjs/no-duplicate-string
      'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
  weight: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.weight.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('Please enter carat weight.'),
  },
  storageLocation: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.storageLocation.label',
    tooltip: 'forms_assets:gemstones.inputs.storageLocation.tooltip',
    value: '',
    required: true,
    yupScheme: yup.string().required('Please enter storage location.'),
  },
};

const PreciousGemstonesFormDetails: PreciousGemstonesFormDetailsType = {
  independentAppraisalValue: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:gemstones.inputs.independentAppraisalValue.label',
    tooltip: 'forms_assets:common.inputs.purchaseValue.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    order: 2,
  },
  appraisalLaboratory: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:gemstones.inputs.appraisalLaboratory.label',
    items: AppraisalLaboratoryList,
    order: 3,
  },
  otherAppraisalLaboratory: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.otherAppraisalLaboratory.label',
    value: '',
    showIf: { appraisalLaboratory: { value: AppraisalLaboratoryType.OTHER } },
    order: 4,
  },
  hasJewelry: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:gemstones.inputs.hasJewelry.label',
    value: false,
    order: 5,
  },
  jewelry: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:gemstones.inputs.jewelry.label',
    items: CollectionName.ASSETS,
    filter: (item) => (item as AssetDto).category === AssetCategory.JEWELLERY,
    dtoType: AssetCategory.JEWELLERY,
    showIf: {
      hasJewelry: { value: true },
    },
    order: 6,
  },
  color: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:gemstones.inputs.color.label',
    items: ColorList,
    order: 7,
  },
  otherColor: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.otherColor.label',
    showIf: {
      color: {
        value: ColorType.other,
      },
    },
    order: 8,
  },
  clarity: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:gemstones.inputs.clarity.label',
    items: ClarityList,
    order: 9,
  },
  otherClarity: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.otherClarity.label',
    showIf: {
      clarity: {
        value: ClarityType.other,
      },
    },
    order: 10,
  },
  cut: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:gemstones.inputs.cut.label',
    items: CutAndPolishList,
    order: 11,
  },
  otherCut: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.otherCut.label',
    showIf: {
      cut: {
        value: CutAndPolishType.other,
      },
    },
    order: 12,
  },
  defects: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.defects.label',
    order: 13,
  },
  enhancements: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:gemstones.inputs.enhancements.label',
    tooltip: 'forms_assets:gemstones.inputs.enhancements.tooltip',
    order: 14,
  },
  sizes: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.sizes.label',
    order: 15,
  },
  countryOfOrigin: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:gemstones.inputs.countryOfOrigin.label',
    withSearch: true,
    items: CountriesList,
    order: 16,
  },
  serialNumber: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.serialNumber.label',
    order: 17,
  },
  polish: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:gemstones.inputs.polish.label',
    items: CutAndPolishList,
    order: 18,
  },
  otherPolish: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.otherPolish.label',
    showIf: {
      polish: {
        value: CutAndPolishType.other,
      },
    },
    order: 19,
  },
  shape: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:gemstones.inputs.shape.label',
    items: ShapeList,
    order: 20,
  },
  otherShape: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.otherShape.label',
    showIf: {
      shape: {
        value: ShapeType.other,
      },
    },
    order: 21,
  },
  fluorescence: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:gemstones.inputs.fluorescence.label',
    tooltip: 'forms_assets:gemstones.inputs.fluorescence.tooltip',
    items: FluorescenceList,
    order: 22,
  },
  otherFluorescence: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.otherFluorescence.label',
    showIf: {
      fluorescence: {
        value: FluorescenceType.other,
      },
    },
    order: 23,
  },
  acquisitionDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:gemstones.inputs.acquisitionDate.label',
    order: 24,
  },
  writeOffAccount: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.writeOffAccount.label',
    order: 25,
  },
  purchaseReceipt: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:gemstones.inputs.purchaseReceipt.label',
    items: CollectionName.PRIVATE_DOCUMENTS,
    dtoType: PrivateDocumentCategory.OTHER,
    order: 26,
  },
  internationalCertificate: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:gemstones.inputs.internationalCertificate.label',
    items: CollectionName.PRIVATE_DOCUMENTS,
    mode: 'multiple',
    dtoType: PrivateDocumentCategory.OTHER,
    order: 27,
  },
  additionalAppraisal: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:gemstones.inputs.additionalAppraisal.label',
    items: CollectionName.PRIVATE_DOCUMENTS,
    dtoType: PrivateDocumentCategory.OTHER,
    order: 28,
  },
  historyOfOrigin: {
    type: FormLabelItem.label,
    label: 'forms_assets:gemstones.inputs.historyOfOrigin.label',
    order: 32,
  },
  typeOfHistoryOfOrigin: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:gemstones.inputs.typeOfHistoryOfOrigin.label',
    items: TypeOfHistoryOfOriginList,
    order: 33,
  },
  otherTypeOfHistoryOfOrigin: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.otherTypeOfHistoryOfOrigin.label',
    showIf: {
      typeOfHistoryOfOrigin: {
        value: TypeOfHistoryOfOriginType.other,
      },
    },
    order: 34,
  },
  occasion: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.occasion.label',
    tooltip: 'forms_assets:gemstones.inputs.occasion.tooltip',
    order: 35,
  },
  source: {
    type: QuestionType.TEXT,
    label: 'forms_assets:gemstones.inputs.source.label',
    tooltip: 'forms_assets:gemstones.inputs.source.tooltip',
    order: 36,
  },
  attachments: baseAssetFields.attachments,
};

const PreciousGemstonesForm = [
  PreciousGemstonesFormGeneral,
  PreciousGemstonesFormDetails,
  assetsOwnershipBuilder([
    'contactInfoLabel',
    'contactInfoLabelDescription',
    'ownerAddress',
    'phoneNumber',
    'email',
  ]).form,
  assetsProfitabilityBuilder(['independentAppraisalAmount']),
  assetsStatisticsBuilder([
    'taxAgentWithBroker',
    'taxAgent',
    'publicFundTaxAgent',
    'taxAgentOther',
    'taxAgentWithBrokerOther',
    'publicTaxAgentOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'holderTaxAgent',
    'holderTaxAgentOther',
    'specificsOfTaxation',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];

export { PreciousGemstonesForm };
